export const createConfigureationServicesSlice = (set, get) => ({
  // Selected services configuration (data entered in ConfigureServices screen)
  // Structure: { serviceId: { rowId: { columnKey: quantity } } }
  // Example: { "101": { "carpet_cleaning_rooms": { "clean": 2, "deodorize": 1 } } }
  selectedServicesConfiguration: {},

  // Manually entered promo codes (array of promo_code strings)
  manuallyEnteredPromoCodes: [],
  setServiceConfiguration: (serviceId, data) => {
    set((state) => ({
      selectedServicesConfiguration: {
        ...state.selectedServicesConfiguration,
        [serviceId]: data
      }
    }));
    // Auto-sync cart after updating configuration
    get().syncCartWithConfiguration();
  },

  updateServiceConfiguration: (serviceId, rowId, columnKey, quantity) => {
    set((state) => ({
      selectedServicesConfiguration: {
        ...state.selectedServicesConfiguration,
        [serviceId]: {
          ...state.selectedServicesConfiguration[serviceId],
          [rowId]: {
            ...(state.selectedServicesConfiguration[serviceId]?.[rowId] || {}),
            [columnKey]: quantity
          }
        }
      }
    }));
    // Auto-sync cart after updating configuration
    get().syncCartWithConfiguration();
  },

  clearServiceConfiguration: (serviceId) => {
    set((state) => {
      const { [serviceId]: removed, ...rest } = state.selectedServicesConfiguration;
      return { selectedServicesConfiguration: rest };
    });
    // Auto-sync cart after clearing configuration
    get().syncCartWithConfiguration();
  },

  clearAllServiceConfigurations: () => {
    set({ selectedServicesConfiguration: {} });
    // Auto-sync cart after clearing all configurations
    get().syncCartWithConfiguration();
  },


  // Get configured services formatted as cart line items with addons
  getConfiguredLineItems: () => {
    const state = get();
    const { selectedServicesConfiguration } = state;
    const lineItems = [];

    Object.entries(selectedServicesConfiguration).forEach(([serviceId, rows]) => {
      const service = state.getServiceById(serviceId);
      if (!service) return;

      Object.entries(rows).forEach(([rowId, columns]) => {
        const row = service.form?.rows?.find(r => r.id === rowId);
        if (!row) return;

        // Get column order from service form to determine which is main service vs addons
        const formColumns = service.form?.columns || [];
        const firstColumnKey = formColumns[0]?.label?.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

        // Separate main service (first column) from addons (other columns)
        let mainItem = null;
        const addons = [];

        Object.entries(columns).forEach(([columnKey, quantity]) => {
          if (quantity > 0) {
            // Try to find the price with fallback strategies
            let price = row.prices?.[columnKey];

            // Fallback 1: If not found, try the first word/segment before underscore
            if ((price === undefined || price === null) && columnKey.includes('_')) {
              const firstSegment = columnKey.split('_')[0];
              price = row.prices?.[firstSegment];
            }

            // Fallback 2: If still not found, try removing double underscores (common with "X / Y" labels)
            if ((price === undefined || price === null) && columnKey.includes('__')) {
              const simplified = columnKey.replace(/__.*$/, '');
              price = row.prices?.[simplified];
            }

            // Check if price is defined (allow 0 as valid price)
            if (price !== undefined && price !== null) {
              // Find the column definition
              const columnDef = formColumns.find(
                col => col.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') === columnKey.toLowerCase()
              );
              const columnLabel = columnDef?.label || columnKey;

              const isMainService = columnKey.toLowerCase() === firstColumnKey;

              if (isMainService) {
                // Create main item
                mainItem = {
                  item_id: `${serviceId}_${rowId}_${columnKey}`,
                  label: `${service.name} - ${row.label}`,
                  category: service.name,
                  category_id: serviceId,
                  service_term: serviceId,
                  price_per_unit: parseFloat(price),
                  quantity: quantity,
                  taxable: true,
                  discountable: true,
                  exclude_from_min: false,
                  addons: [] // Will be populated below
                };
              } else {
                // Create addon (cart expects addon_price instead of price_per_unit)
                addons.push({
                  addon_id: `${serviceId}_${rowId}_${columnKey}`,
                  addon_name: columnLabel,
                  category: service.name,
                  category_id: serviceId,
                  service_term: serviceId,
                  addon_price: parseFloat(price),
                  quantity: quantity,
                  taxable: true,
                  discountable: false,
                  exclude_from_min: false
                });
              }
            }
          }
        });

        // Add main item with its addons
        if (mainItem) {
          mainItem.addons = addons;
          lineItems.push(mainItem);
        }
      });
    });

    // Dryer Vent uses the discounted "w/ ADC" rate when Air Duct is also ordered.
    const dryerItem = lineItems.find(item => String(item.service_term) === '262');
    const hasAirDuct = lineItems.some(item => String(item.service_term) === '99');

    if (dryerItem && hasAirDuct) {
      const adcPrice = parseFloat(state.currentFranchise?.prices?.field_dryer_vent_cleaning_adc);
      if (!Number.isNaN(adcPrice)) {
        dryerItem.price_per_unit = adcPrice;
      }
    }

    return lineItems;
  },

  // Get eligible promotions that should be auto-applied based on cart contents
  // Add a manually entered promo code
  addManualPromoCode: (promoCode) => {
    const state = get();
    if (!state.manuallyEnteredPromoCodes.includes(promoCode)) {
      set((prevState) => ({
        manuallyEnteredPromoCodes: [...prevState.manuallyEnteredPromoCodes, promoCode]
      }));
      // Trigger cart sync after adding promo code
      get().syncCartWithConfiguration();
    }
  },

  // Remove a manually entered promo code
  removeManualPromoCode: (promoCode) => {
    set((state) => ({
      manuallyEnteredPromoCodes: state.manuallyEnteredPromoCodes.filter(code => code !== promoCode)
    }));
    // Trigger cart sync after removing promo code
    get().syncCartWithConfiguration();
  },

  getEligiblePromotions: () => {
    const state = get();
    const { currentFranchise, manuallyEnteredPromoCodes } = state;

    if (!currentFranchise?.promotions) {
      return [];
    }

    // Filter promotions that are eligible
    const eligiblePromotions = currentFranchise.promotions
      .filter(promo => {
        // Include if:
        // 1. Not excluded (automatic promotions), OR
        // 2. Manually entered by user
        const isManuallyEntered = manuallyEnteredPromoCodes.includes(promo.promo_code);
        // Check for falsy exclude values (false, 0, "0", null, undefined)
        const isNotExcluded = promo.exclude !== true && promo.exclude !== "1" && promo.exclude !== 1;
        return isNotExcluded || isManuallyEntered;
      })
      .map(promo => ({
        ...promo,
        id: promo.promo_code,
        text: promo.title,
        category: Object.values(promo.service || {}),
        conditions: promo.conditions || {},
        min_amount: Number(promo.min_amount || 0),
        prices: promo.prices || [],
        priority: promo.priority || 0,
        non_combinable: promo.non_combinable || false,
        allow_multiple_category_discounts: promo.allow_multiple_category_discounts !== false,
        allow_multiple_order_discounts: promo.allow_multiple_order_discounts !== false,
        excludes: promo.excludes || [],
        override: promo.override === "1"
      }));

    return eligiblePromotions;
  },

  // Automatically sync cart with configured services and promotions
  syncCartWithConfiguration: async () => {
    const state = get();
    const lineItems = state.getConfiguredLineItems();
    const eligiblePromotions = state.getEligiblePromotions();

    // This sets services items AND applies discounts in a single update cycle.
    // Was getting too many re-renders when done separately.
    await state.setCartItemsWithDiscounts(lineItems, eligiblePromotions);
  },
});
